import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChefHat, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO } from "@/lib/brand";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(apiErr(err));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/29394552/pexels-photo-29394552.jpeg" alt=""
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-10 left-10 text-[#FDFBF7]">
          <h2 className="font-heading text-4xl font-medium">Welcome back to your kitchen.</h2>
          <p className="mt-2 text-[#FDFBF7]/80 max-w-md">Sign in to manage menus, staff, inventory and sales.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-block mb-8">
            <img src={LOGO} alt="manage.kitchen" className="h-16 w-auto object-contain" />
          </Link>
          <h1 className="font-heading text-3xl font-medium">Log in</h1>
          <p className="text-muted-foreground mt-1 text-sm">Enter your credentials to continue.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@restaurant.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full bg-primary hover:bg-[#B35A42] text-primary-foreground">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
            </Button>
          </form>
          <div className="mt-4 text-xs text-muted-foreground bg-muted rounded-lg p-3">
            Demo account — <span className="font-semibold">demo@kitchenpro.com</span> / <span className="font-semibold">Demo123!</span>
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link to="/register" className="text-primary font-medium hover:underline">Start free trial</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
